import {
  ICatalog,
  ICategory,
  ICharge,
  IDeal,
  IDiscount,
  IOption,
  IOptionList,
  IProduct,
  ISku,
  isSku,
} from "shared/hubrise/models"
import { Option, Product, Sku } from "shared/hubrise/utils"
import { nextItemInArray } from "shared/utils/javascript"

// ResourceMap and ResourceType are implementation details and should not be exported
type ResourceMap = {
  categoryOrSku: ICategory | ISku | null
  option: IOption | null
  deal: IDeal | null
  discount: IDiscount | null
  charge: ICharge | null
}
type ResourceType = keyof ResourceMap

export class LastVisits {
  public lastResourceType: ResourceType | undefined = undefined
  private _resourceMap: ResourceMap = { categoryOrSku: null, option: null, deal: null, discount: null, charge: null }

  constructor(private _catalog: ICatalog) {}

  visit(
    ...modelTypeAndModel:
      | ["product", IProduct | undefined]
      | ["sku", ISku | undefined]
      | ["category", ICategory | undefined]
      | ["optionList", IOptionList | undefined]
      | ["option", IOption | undefined]
      | ["deal", IDeal | undefined]
      | ["discount", IDiscount | undefined]
      | ["charge", ICharge | undefined]
  ): void {
    const [modelType, model] = modelTypeAndModel
    switch (modelType) {
      case "product":
        this.lastResourceType = "categoryOrSku"
        this._resourceMap.categoryOrSku = model ? model.skus[0] : null
        break
      case "sku":
        this.lastResourceType = "categoryOrSku"
        this._resourceMap.categoryOrSku = model || null
        break
      case "category":
        this.lastResourceType = "categoryOrSku"
        this._resourceMap.categoryOrSku = model || null
        break
      case "optionList":
        this.lastResourceType = "option"
        this._resourceMap.option = model ? model.options[0] : null
        break
      case "option":
        this.lastResourceType = "option"
        this._resourceMap.option = model || null
        break
      case "deal":
        this.lastResourceType = "deal"
        this._resourceMap.deal = model || null
        break
      case "discount":
        this.lastResourceType = "discount"
        this._resourceMap.discount = model || null
        break
      case "charge":
        this.lastResourceType = "charge"
        this._resourceMap.charge = model || null
        break
    }
  }

  get category(): ICategory | null {
    if (this._resourceMap.categoryOrSku !== null && !isSku(this._resourceMap.categoryOrSku)) {
      return this._resourceMap.categoryOrSku
    }
    return null
  }

  get sku(): ISku | null {
    if (this._resourceMap.categoryOrSku !== null && isSku(this._resourceMap.categoryOrSku)) {
      return this._resourceMap.categoryOrSku
    }
    return null
  }

  get product(): IProduct | null {
    try {
      return this.sku ? Sku.findWithProductById(this._catalog, this.sku.id).product : null
    } catch {
      return null
    }
  }

  get categoryOrSku(): ICategory | ISku | null {
    return this._resourceMap.categoryOrSku
  }

  get categoryOrProduct(): ICategory | IProduct | null {
    return this.category || this.product
  }

  get option(): IOption | null {
    return this._resourceMap.option
  }

  get optionList(): IOptionList | null {
    try {
      return this.option ? Option.findWithOptionListById(this._catalog, this.option.id).optionList : null
    } catch {
      return null
    }
  }

  get deal(): IDeal | null {
    return this._resourceMap.deal
  }

  get discount(): IDiscount | null {
    return this._resourceMap.discount
  }

  get charge(): ICharge | null {
    return this._resourceMap.charge
  }

  get isLastVisitCategory(): boolean {
    return (
      this.lastResourceType === "categoryOrSku" &&
      this._resourceMap.categoryOrSku !== null &&
      !isSku(this._resourceMap.categoryOrSku)
    )
  }

  get isLastVisitSku(): boolean {
    return (
      this.lastResourceType === "categoryOrSku" &&
      this._resourceMap.categoryOrSku !== null &&
      isSku(this._resourceMap.categoryOrSku)
    )
  }

  get isLastVisitProduct(): boolean {
    return this.isLastVisitSku
  }

  get isLastVisitOption(): boolean {
    return this.lastResourceType === "option"
  }

  get isLastVisitOptionList(): boolean {
    return this.isLastVisitOption
  }

  get isLastVisitDeal(): boolean {
    return this.lastResourceType === "deal"
  }

  get isLastVisitDiscount(): boolean {
    return this.lastResourceType === "discount"
  }

  get isLastVisitCharge(): boolean {
    return this.lastResourceType === "charge"
  }

  get nextCategoryOrProduct(): ICategory | IProduct | null {
    return nextItemInArray(Product.sortedCategoriesAndProducts(this._catalog), this.categoryOrProduct) || null
  }

  get nextOptionList(): IOptionList | null {
    return nextItemInArray(this._catalog.data.optionLists, this.optionList) || null
  }

  get nextDeal(): IDeal | null {
    return nextItemInArray(this._catalog.data.deals, this.deal) || null
  }

  get nextDiscount(): IDiscount | null {
    return nextItemInArray(this._catalog.data.discounts, this.discount) || null
  }

  get nextCharge(): ICharge | null {
    return nextItemInArray(this._catalog.data.charges, this.charge) || null
  }
}
